import { useQuery } from '@tanstack/react-query';
import { Loading } from 'corso-admin-ui';
import { CrewMerchantUi } from 'corso-types';
import { createContext, PropsWithChildren, useContext } from 'react';
import { z } from 'zod';
import api from '~/api';
import PageStatus from '~/components/PageStatus';
import { usePathParams } from '~/hooks/usePathParams';
import { useStoreId } from '~/providers/StoreUserProvider';

function useProductQuery() {
  const { storeProductId } = usePathParams(
    z.object({ storeProductId: z.coerce.number().optional() }),
  );
  const storeId = useStoreId();

  return useQuery({
    enabled: !!storeProductId,
    throwOnError: false,
    queryKey: ['product', storeProductId, storeId],
    queryFn: () => {
      if (!storeProductId) throw new Error('Missing Product ID');
      return api.store(storeId).product.get({
        storeProductId: `${storeProductId}`,
      });
    },
  });
}

const ProductContext = createContext<CrewMerchantUi.StoreProductDetail | null>(
  null,
);

export function useProduct() {
  const product = useContext(ProductContext);
  if (!product) {
    throw new Error('useProduct must be used within a ProductProvider');
  }
  return product;
}

export function ProductProvider({ children }: PropsWithChildren) {
  const { data: product, error, isError, isPending } = useProductQuery();

  if (isError) return <PageStatus.Error error={error} />;

  if (isPending) {
    return (
      <>
        <Loading />
        <PageStatus.Loading />
      </>
    );
  }

  return (
    <ProductContext.Provider value={product}>
      {children}
    </ProductContext.Provider>
  );
}
